/**
 * Product image uploads.
 *
 * Images used to be base64'd into the product save. That cost +33% on the wire
 * and every image had to fit inside the backend's `express.json({ limit: "11mb" })`
 * alongside the rest of the product, so a couple of phone photos 413'd the whole
 * save. It also meant the only way to fit more images was to re-encode them
 * smaller, which throws away quality the merchant paid for.
 *
 * Now the file goes straight to the CDN as multipart form data, using a
 * single-use write permission minted by our own API (which picks the
 * destination path, so the browser cannot choose where it writes). The product
 * only ever stores the returned basename. Originals are preserved byte for
 * byte: no resizing, no re-encoding, no quality setting.
 */

/** Product-level image cap. Mirrors `.max(20)` in the backend's productSchemas.js. */
export const MAX_PRODUCT_IMAGES = 20;

/** Per-variant image cap. Mirrors `variantSchema.images.max(20)` in the same file. */
export const MAX_VARIANT_IMAGES = 20;

/** Largest original accepted. Mirrors UPLOAD_IMAGE_MAX_BYTES in the backend. */
export const MAX_SOURCE_BYTES = 10 * 1024 * 1024;

/** Extensions the backend's /upload-image endpoint will mint a permission for. */
const ALLOWED_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'webp', 'gif', 'avif']);

export type RejectReason = 'type' | 'size';

/**
 * Why a file cannot be uploaded, or null when it can. Checked before asking the
 * API for a permission so an obviously bad file never costs a round trip.
 */
export function rejectReasonFor(file: File): RejectReason | null {
  const extension = (file.name.split('.').pop() || '').toLowerCase();
  if (!file.type.startsWith('image/') || !ALLOWED_EXTENSIONS.has(extension)) return 'type';
  if (file.size > MAX_SOURCE_BYTES) return 'size';
  return null;
}

/**
 * Legacy helper: approximate decoded byte count of a base64 data URL.
 *
 * Nothing uploads base64 any more, but variant images saved before this change
 * are still stored as data URLs on the product document, so opening one for
 * edit loads them into form state and sends them back. This is what keeps that
 * case from silently 413'ing.
 */
export function dataUrlBytes(value: string): number {
  if (!value.startsWith('data:')) return 0; // a CDN filename, not inline bytes
  const base64 = value.slice(value.indexOf(',') + 1);
  return Math.floor((base64.length * 3) / 4);
}

/** Total inline-bytes weight of a list that may mix filenames and legacy data URLs. */
export function totalUploadBytes(values: string[]): number {
  return values.reduce((sum, v) => sum + dataUrlBytes(v), 0);
}

/**
 * Budget for one save. The server accepts an 11 MB body; only legacy inline
 * variant images count against it now.
 */
export const MAX_UPLOAD_BYTES = 9 * 1024 * 1024;
